import { useContext, createContext, useReducer, useMemo, FC } from 'react'
import type { CustomerPaymentTokens } from '@pylot-data/pylotschema'

type SignUpDetail = {
    firstname: string
    lastname: string
}

type PageType = {
    transparentHeader: boolean
    isNicotinePage: boolean
}

export interface State {
    scrollToReview?: boolean
    signUpDetail?: SignUpDetail | null
    isKlarnaAuthorized?: boolean
    toggleYotpoReviewPageWidget?: boolean
    isSocialLoading?: boolean
    isGuestAddressFormVisible?: boolean
    isCreditSelected?: string
    savedCards?: CustomerPaymentTokens | null
    isNicotinePage?: boolean
    isCookiesAccepted?: boolean
    isStoreLocatorEnabled?: boolean
    pageType?: PageType
    isSpreedlyFatalError?: boolean
}

interface Values extends State {
    setScrollToReview: (scrollToReview?: boolean) => void
    setSignUpDetail: (signUpDetail?: SignUpDetail | null) => void
    setIsKlarnaAuthorized: (isKlarnaAuthorized?: boolean) => void
    setToggleReinitYotpoReviewPageWidget: (
        toggleYotpoReviewPageWidget?: boolean
    ) => void
    setSocialLoader: (isSocialLoading?: boolean) => void
    setGuestAddressFormVisible: (isGuestAddressFormVisible?: boolean) => void
    setIsCreditSelected: (isCreditSelected?: string) => void
    setSavedCards: (savedCards?: CustomerPaymentTokens | null) => void
    setIsNicotinePage: (isNicotinePage?: boolean) => void
    setIsCookiesAccepted: (isCookiesAccepted?: boolean) => void
    setIsStoreLocatorEnabled: (isStoreLocatorEnabled?: boolean) => void
    setPageType: (pageType?: PageType) => void
    setIsSpreedlyFatalError: (isSpreedlyFatalError: boolean) => void
}

interface Action extends State {
    type:
        | 'IS_GUEST_ADDRESS_FORM_VISIBLE'
        | 'SCROLL_TO_REVIEW'
        | 'SIGN_UP_DETAIL'
        | 'IS_KLARNA_AUTHORISED'
        | 'REINIT_YOTPO_REVIEW_PAGE_WIDGET'
        | 'IS_CREDIT_SELECTED'
        | 'SOCIAL_LOGIN_LOADER'
        | 'SAVE_CARDS'
        | 'SET_STORE_LOCATOR_DATA'
        | 'PAGE_TYPE'
        | 'IS_SPREEDLY_FATAL_ERROR'
}

const initialState = {
    isGuestAddressFormVisible: false,
    scrollToReview: false,
    signUpDetail: null,
    isKlarnaAuthorized: false,
    toggleYotpoReviewPageWidget: false,
    isCreditSelected: '',
    isSocialLoading: false,
    savedCards: null,
    isStoreLocatorEnabled: false,
    pageType: {
        transparentHeader: false,
        isNicotinePage: true
    },
    isSpreedlyFatalError: false
}

const authReducer = (state: State, action: Action) => {
    switch (action.type) {
        case 'IS_GUEST_ADDRESS_FORM_VISIBLE': {
            return {
                ...state,
                isGuestAddressFormVisible: action.isGuestAddressFormVisible
            }
        }

        case 'SCROLL_TO_REVIEW': {
            return {
                ...state,
                scrollToReview: action.scrollToReview
            }
        }
        case 'SIGN_UP_DETAIL': {
            return {
                ...state,
                signUpDetail: action.signUpDetail
            }
        }
        case 'IS_KLARNA_AUTHORISED': {
            return {
                ...state,
                isKlarnaAuthorized: action.isKlarnaAuthorized
            }
        }
        case 'REINIT_YOTPO_REVIEW_PAGE_WIDGET': {
            return {
                ...state,
                toggleYotpoReviewPageWidget: action.toggleYotpoReviewPageWidget
            }
        }
        case 'IS_CREDIT_SELECTED': {
            return {
                ...state,
                isCreditSelected: action.isCreditSelected
            }
        }
        case 'SOCIAL_LOGIN_LOADER': {
            return {
                ...state,
                isSocialLoading: action.isSocialLoading
            }
        }
        case 'SAVE_CARDS': {
            return {
                ...state,
                savedCards: action.savedCards
            }
        }
        case 'SET_STORE_LOCATOR_DATA': {
            return {
                ...state,
                isStoreLocatorEnabled: action.isStoreLocatorEnabled
            }
        }
        case 'PAGE_TYPE': {
            return {
                ...state,
                pageType: action.pageType
            }
        }
        case 'IS_SPREEDLY_FATAL_ERROR': {
            return {
                ...state,
                isSpreedlyFatalError: action.isSpreedlyFatalError
            }
        }
    }
}

const ReviewContext = createContext(initialState as Values)

export const useAlfakherContext = () => useContext(ReviewContext)

export const AlfakherContext: FC = ({ children }) => {
    const [state, dispatch] = useReducer(authReducer, initialState)

    const setGuestAddressFormVisible = (isGuestAddressFormVisible: boolean) =>
        dispatch({
            type: 'IS_GUEST_ADDRESS_FORM_VISIBLE',
            isGuestAddressFormVisible
        })

    const setScrollToReview = (scrollToReview = true) =>
        dispatch({ type: 'SCROLL_TO_REVIEW', scrollToReview })

    const setIsKlarnaAuthorized = (isKlarnaAuthorized: boolean) =>
        dispatch({ type: 'IS_KLARNA_AUTHORISED', isKlarnaAuthorized })

    const setSignUpDetail = (signUpDetail: SignUpDetail) =>
        dispatch({ type: 'SIGN_UP_DETAIL', signUpDetail })

    const setToggleReinitYotpoReviewPageWidget = (
        toggleYotpoReviewPageWidget = true
    ) =>
        dispatch({
            type: 'REINIT_YOTPO_REVIEW_PAGE_WIDGET',
            toggleYotpoReviewPageWidget
        })
    const setIsCreditSelected = (isCreditSelected: string) =>
        dispatch({
            type: 'IS_CREDIT_SELECTED',
            isCreditSelected
        })

    const setSocialLoader = (isSocialLoading = true) =>
        dispatch({ type: 'SOCIAL_LOGIN_LOADER', isSocialLoading })

    const setSavedCards = (savedCards: CustomerPaymentTokens) =>
        dispatch({ type: 'SAVE_CARDS', savedCards })

    const setIsStoreLocatorEnabled = (isStoreLocatorEnabled: boolean) => {
        dispatch({ type: 'SET_STORE_LOCATOR_DATA', isStoreLocatorEnabled })
    }
    const setPageType = (pageType: PageType) =>
        dispatch({ type: 'PAGE_TYPE', pageType })

    const setIsSpreedlyFatalError = (isSpreedlyFatalError: boolean) =>
        dispatch({ type: 'IS_SPREEDLY_FATAL_ERROR', isSpreedlyFatalError })

    const value = useMemo(
        () => ({
            ...state,
            setIsCreditSelected,
            setGuestAddressFormVisible,
            setScrollToReview,
            setSignUpDetail,
            setIsKlarnaAuthorized,
            setToggleReinitYotpoReviewPageWidget,
            setSocialLoader,
            setSavedCards,
            setIsStoreLocatorEnabled,
            setPageType,
            setIsSpreedlyFatalError
        }),
        [state]
    )
    return (
        <ReviewContext.Provider value={value as Values}>
            {children}
        </ReviewContext.Provider>
    )
}
